import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import InstallDesktopRoundedIcon from '@mui/icons-material/InstallDesktopRounded'
import { Chip, Tooltip } from '@mui/material'

/**
 * Simplified, recognizable brand marks for the debug panel's device rows and activity
 * timeline. Hand-drawn SVGs (no asset/network dependency) — at 16-20px the simplified
 * geometry reads as the real logos. Unknown browsers fall back to a globe.
 */

function ChromeMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Chrome">
      <circle cx="24" cy="24" r="20" fill="#fff" />
      <path fill="#EA4335" d="M24 4a20 20 0 0 1 17.32 10H24a10 10 0 0 0-9.4 6.6L7.6 10.2A19.98 19.98 0 0 1 24 4z" />
      <path fill="#FBBC05" d="M7.6 10.2 14.6 22.4a10 10 0 0 0 8 11.5L15.5 43.2A20 20 0 0 1 7.6 10.2z" />
      <path fill="#34A853" d="M15.5 43.2 22.6 33.9a10 10 0 0 0 10.8-5.9h8A20 20 0 0 1 15.5 43.2z" />
      <path fill="#4285F4" d="M41.32 14A20 20 0 0 1 41.4 28h-8a10 10 0 0 0-1.4-9.6z" opacity="0" />
      <circle cx="24" cy="24" r="9" fill="#4285F4" stroke="#fff" strokeWidth="2.4" />
    </svg>
  )
}

function SafariMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Safari">
      <circle cx="24" cy="24" r="20" fill="#1B88E5" />
      <circle cx="24" cy="24" r="17" fill="#F3F8FE" />
      <g transform="rotate(-45 24 24)">
        <polygon points="24,10 27.5,24 20.5,24" fill="#FF3B30" />
        <polygon points="24,38 20.5,24 27.5,24" fill="#B0B8C1" />
      </g>
    </svg>
  )
}

function FirefoxMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Firefox">
      <circle cx="24" cy="24" r="20" fill="#FF7139" />
      <path
        fill="#FFB53A"
        d="M24 8c8 0 14 6 14 14 0 9-7 16-16 16 6-2 10-7 10-13 0-3-1-5-3-7-1 3-3 4-5 4 2-4 1-9-3-12 1-1 2-2 3-2z"
      />
      <circle cx="24" cy="24" r="20" fill="none" stroke="#E55B13" strokeWidth="1" opacity="0.4" />
    </svg>
  )
}

function EdgeMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Edge">
      <circle cx="24" cy="24" r="20" fill="#0C59A4" />
      <path
        fill="#2CC3D2"
        d="M10 28c0-9 7-16 16-16 7 0 12 4 13 10-3-2-6-3-10-3-8 0-14 5-14 12 0 2 .4 3.6 1.2 5C12.4 33.6 10 31 10 28z"
      />
      <path
        fill="#7DF9E7"
        d="M16 31c0-6 5-10 11-10 4 0 8 1.6 10 4-1 7-7 13-15 13-2 0-4-.4-5.6-1.2C16.8 35 16 33 16 31z"
        opacity="0.9"
      />
    </svg>
  )
}

function OperaMark({ size }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Opera">
      <circle cx="24" cy="24" r="20" fill="#FF1B2D" />
      <ellipse cx="24" cy="24" rx="9" ry="13" fill="#fff" />
      <ellipse cx="24" cy="24" rx="5.5" ry="10" fill="#FF1B2D" />
    </svg>
  )
}

const MARKS = {
  chrome: ChromeMark,
  safari: SafariMark,
  firefox: FirefoxMark,
  edge: EdgeMark,
  opera: OperaMark,
  samsung: ChromeMark, // chromium-based; close enough at icon size
  brave: ChromeMark,
}

export default function BrowserIcon({ browserName, size = 18 }) {
  const Mark = MARKS[String(browserName || '').toLowerCase()]
  if (!Mark) {
    return (
      <Tooltip title={browserName || 'Unknown browser'}>
        <PublicRoundedIcon sx={{ fontSize: size, color: 'text.secondary' }} />
      </Tooltip>
    )
  }
  return (
    <Tooltip title={browserName}>
      <span style={{ display: 'inline-flex', lineHeight: 0 }}>
        <Mark size={size} />
      </span>
    </Tooltip>
  )
}

/** Small "installed app" badge shown next to the browser mark when the client runs as a PWA. */
export function PwaBadge({ pwa }) {
  if (!pwa) return null
  return (
    <Tooltip title="Running as an installed app (PWA)">
      <Chip
        size="small"
        icon={<InstallDesktopRoundedIcon sx={{ fontSize: 13 }} />}
        label="PWA"
        sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
        color="info"
        variant="outlined"
      />
    </Tooltip>
  )
}
